/**
 * TagsFormField — a list of free-text values (policy tags, allowed provider tools) as chips with an add input.
 * Template example: demo `/form-elements` (input beside removable badges). A form field binder, so the value, its
 * schema errors and its server errors all live in the form.
 */
import { X } from 'lucide-react';
import { useState } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { FormDescription, FormFieldError, type FormFieldBaseProps, FormLabel } from '../../../components/form/index.js';
import { Input } from '../../../components/tailgrids/core/input.js';
import { TextField } from '../../../components/tailgrids/core/text-field.js';
import { Button, Chip, type ChipTone } from '../../../components/ui/index.js';
import { cn } from '../../../utils/cn.js';

interface TagsFormFieldProps<TValues extends FieldValues> extends FormFieldBaseProps<TValues> {
  placeholder?: string;
  tone?: ChipTone;
  /** Shown instead of the chips when the list is empty and the field is read-only. */
  emptyText?: string;
}

export function TagsFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  disabled,
  className,
  placeholder = 'Type a value and press Enter',
  tone = 'neutral',
  emptyText = 'None',
}: TagsFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const [draft, setDraft] = useState('');
  const tags: string[] = Array.isArray(field.value) ? field.value : [];
  const locked = disabled || field.disabled;

  const commit = () => {
    const tag = draft.trim().replace(/,$/, '');
    setDraft('');
    if (tag && !tags.includes(tag)) field.onChange([...tags, tag]);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <TextField
        value={draft}
        onChange={setDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commit();
          }
        }}
        onBlur={() => {
          commit();
          field.onBlur();
        }}
        invalid={fieldState.invalid}
        disabled={locked}
        className="w-full flex-col gap-1.5"
        validationBehavior="aria"
      >
        <FormLabel label={label} help={help} />
        {!locked && <Input ref={field.ref} placeholder={placeholder} className="w-full px-3 py-2.5 text-sm" />}
        <FormDescription>{description}</FormDescription>
        <FormFieldError message={fieldState.error?.message} />
      </TextField>
      <ul className="flex flex-wrap items-center gap-1.5" aria-label={typeof label === 'string' ? label : undefined}>
        {tags.length === 0 && locked && <li className="text-sm text-muted">{emptyText}</li>}
        {tags.map((tag) => (
          <li key={tag} className="inline-flex items-center gap-1">
            <Chip tone={tone}>{tag}</Chip>
            {!locked && (
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={`Remove ${tag}`}
                onClick={() => field.onChange(tags.filter((t) => t !== tag))}
              >
                <X size={12} aria-hidden="true" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
